import { CommonDialogs } from "@mcleod/common";
import { DataSourceMode, DataSourceModeChangeEvent, LookupModelSearchEvent, LookupModelSelectionEvent, DataSourceExecutionEvent,
         TableRowDisplayEvent, ChangeEvent, Textbox, TableRowExpansionEvent, Layout, Table, TableRow, TableRowMode } from "@mcleod/components";
import { Api } from "@mcleod/core";
import { AutogenLayoutMobileCommSendLoadOptions } from "./autogen/AutogenLayoutMobileCommSendLoadOptions";
import { ComponentVisibilityManager, UIComponents } from "./managers/ComponentVisibilityManager";
import { VendorStrategyFactory } from "./strategies/VendorStrategy";
import { VendorType } from "./types/VendorConfiguration";

/**
 * Mobile Communication Send Load Options component
 * Manages vendor-specific UI configuration and behavior for mobile communication settings
 */
export class MobileCommSendLoadOptions extends AutogenLayoutMobileCommSendLoadOptions {
    private componentManager: ComponentVisibilityManager;
    public mcControlId: string;

    // Constants
    private static readonly FIELD_NAMES = {
        MC_CONTROL_ID: 'mc_control_id',
        LOCATION_ID: 'location_id',
        ID: 'id'
    } as const;

    private static readonly FORM_TYPES = {
        INPUT: 'I',
        OUTPUT: 'O'
    } as const;

    private static readonly API_ENDPOINTS = {
        SEND_LOAD_OPTIONS: 'lme/dispatch/mobilecomm/mc-send-load-options'
    } as const;

    private static readonly ERROR_MESSAGES = {
        DUPLICATE_RECORD: 'A send load option record for this mobile communication control and location already exists.',
        INITIALIZATION_ERROR: 'An error occurred while initializing the component. Please refresh the page.',
        VALIDATION_ERROR: 'An error occurred while validating the record. Please try again.',
        SELECTION_ERROR: 'An error occurred while processing the selection. Please try again.',
        VENDOR_CONFIG_ERROR: 'Error applying vendor configuration'
    } as const;

    /**
     * Initializes the component and sets up event listeners
     */
    async onLoad(): Promise<void> {
        try {
            this.initializeComponentManager();
            this.setupEventListeners();
            this.setupDataSourceValidation();
        } catch (error) {
            console.error('Error during component initialization:', error);
            CommonDialogs.showError(MobileCommSendLoadOptions.ERROR_MESSAGES.INITIALIZATION_ERROR);
        }
    }

    // =============================================================================
    // INITIALIZATION METHODS
    // =============================================================================

    /**
     * Sets up data source validation for preventing duplicate records
     */
    private setupDataSourceValidation(): void {
        this.mainDataSource.doBeforeDataSourcePost = async () => {
            try {
                const controlId = this.getActiveRowString(MobileCommSendLoadOptions.FIELD_NAMES.MC_CONTROL_ID);
                if (!controlId) {
                    CommonDialogs.showError('Mobile control ID is required.');
                    return false;
                }

                const locationId = this.normalizeLocationId(
                    this.getActiveRowString(MobileCommSendLoadOptions.FIELD_NAMES.LOCATION_ID)
                );

                if (this.mainDataSource.mode === DataSourceMode.ADD) {
                    const isDuplicate = await this.checkForDuplicateRecord(controlId, locationId);
                    if (isDuplicate) {
                        CommonDialogs.showError(MobileCommSendLoadOptions.ERROR_MESSAGES.DUPLICATE_RECORD);
                        return false;
                    }
                }

                return true;
            } catch (error) {
                console.error('Error validating send load options:', error);
                CommonDialogs.showError(MobileCommSendLoadOptions.ERROR_MESSAGES.VALIDATION_ERROR);
                return false;
            }
        };
    }

    // =============================================================================
    // EVENT HANDLERS
    // =============================================================================

    /**
     * Handles data source mode changes, particularly for UPDATE mode
     * @param event - The mode change event
     */
    sourceSendLoadOptionsAfterModeChange(event: DataSourceModeChangeEvent): void {
        if (event.newMode === DataSourceMode.UPDATE || event.newMode === DataSourceMode.ADD) {
            const mcControlId = this.getActiveRowString(MobileCommSendLoadOptions.FIELD_NAMES.MC_CONTROL_ID);
            this.applyVendorConfiguration(mcControlId);
            this.componentManager?.applyUpdateModeConfiguration(mcControlId);
        }
    }

    /**
     * Handles changes to the arrived delay textbox, limiting input to 9 characters
     * @param event - The change event
     */
    textboxOnChange(event: any): void {
        const textbox = event.target as Textbox;
        if (textbox.text.length > 9) {
            textbox.text = textbox.text.substring(0, 9);
        }
    }

    /**
     * Initializes the component visibility manager with references to UI components
     */
    private initializeComponentManager(): void {
        const uiComponents = this.createUIComponentsConfiguration();
        this.componentManager = new ComponentVisibilityManager(uiComponents);
    }

    /**
     * Sets up event listeners for component interactions
     */
    private setupEventListeners(): void {
        this.textboxMcControlId?.addLookupModelSelectionListener(async (event: LookupModelSelectionEvent) => {
            try {
                if (event?.hasSelection()) {
                    const mcControlId = event.target?.text;
                    this.applyVendorConfiguration(mcControlId);
                }
            } catch (error) {
                console.error('Error handling mobile control ID selection:', error);
                CommonDialogs.showError(MobileCommSendLoadOptions.ERROR_MESSAGES.SELECTION_ERROR);
            }
        });
    }

    // =============================================================================
    // VENDOR CONFIGURATION METHODS
    // =============================================================================

    /**
     * Applies vendor-specific configuration based on the control ID
     * @param mcControlId - The mobile communication control ID that determines vendor type
     */
    public applyVendorConfiguration(mcControlId?: string | null): void {
        try {
            if (!mcControlId || typeof mcControlId !== 'string') {
                this.componentManager?.hideAllComponents();
                return;
            }

            const strategy = VendorStrategyFactory.getStrategy(mcControlId);
            if (!strategy) {
                this.componentManager?.hideAllComponents();
                return;
            }

            const config = strategy.getConfiguration();
            const locationId = this.textboxLocationId?.text || '';

            // Special case for Peoplenet. Trip data field value also has to be checked.
            config.tabs.tripData = config.prefix == "PEO" && "Y" === this.mainDataSource?.activeRow?.get("trip_data");

            this.componentManager?.applyVendorConfiguration(config, locationId);

            // Handle trip data visibility for Pegasus
            const isPegasus = strategy.getVendorType() === VendorType.PEGASUS;
            this.componentManager?.handleTripDataVisibility(isPegasus);
        } catch (error) {
            console.error(MobileCommSendLoadOptions.ERROR_MESSAGES.VENDOR_CONFIG_ERROR, error);
            // Fallback to hiding all components if there's an error
            this.componentManager?.hideAllComponents();
        }
    }

    /**
     * Legacy method name for backward compatibility
     * @deprecated Use applyVendorConfiguration instead
     */
    changeVendor(vendor: string): void {
        this.applyVendorConfiguration(vendor);
    }

    /**
     * Handles form lookup model search filtering
     * Filters forms by type 'O' (Output) and optionally by mobile control ID
     * @param event - The lookup model search event
     */
    textboxFormBeforeLookupModelSearch(event: LookupModelSearchEvent): void {
        if (!event?.filter) return;

        event.filter.form_type = MobileCommSendLoadOptions.FORM_TYPES.OUTPUT;

        const activeRowId = this.mainDataSource?.activeRow?.get(MobileCommSendLoadOptions.FIELD_NAMES.ID);
        if (activeRowId) {
            event.filter.mc_control_id = this.mainDataSource.activeRow.get(
                MobileCommSendLoadOptions.FIELD_NAMES.MC_CONTROL_ID
            );
        }
    }

    textboxMcControlIdBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.is_active = "Y";
    }

    // =============================================================================
    // HELPER METHODS
    // =============================================================================

    /**
     * Safely gets a string value from the active row
     * @param fieldName - The field name to retrieve
     * @returns The string value or empty string if not found
     */
    private getActiveRowString(fieldName: string): string {
        return this.mainDataSource?.activeRow?.getString(fieldName) || '';
    }

    /**
     * Normalizes location ID value (converts undefined to null)
     * @param locationId - The location ID to normalize
     * @returns Normalized location ID
     */
    private normalizeLocationId(locationId: string | undefined): string | null {
        return locationId === undefined ? null : locationId;
    }

    /**
     * Checks for duplicate records in the database
     * @param controlId - The mobile control ID
     * @param locationId - The location ID (can be null)
     * @returns Promise<boolean> - True if duplicate exists, false otherwise
     */
    private async checkForDuplicateRecord(controlId: string, locationId: string | null): Promise<boolean> {
        const searchParams: Record<string, string> = {
            [MobileCommSendLoadOptions.FIELD_NAMES.MC_CONTROL_ID]: controlId
        };

        // Only include location_id if it has a valid value
        if (locationId !== null && locationId !== '') {
            searchParams[MobileCommSendLoadOptions.FIELD_NAMES.LOCATION_ID] = locationId;
        }

        const response = await Api.search(
            MobileCommSendLoadOptions.API_ENDPOINTS.SEND_LOAD_OPTIONS,
            searchParams
        );

        return this.evaluateDuplicateResponse(response, locationId);
    }

    /**
     * Evaluates the API response to determine if a duplicate record exists
     * @param response - The API search response
     * @param locationId - The location ID being validated
     * @returns boolean - True if duplicate exists, false otherwise
     */
    private evaluateDuplicateResponse(response: any, locationId: string | null): boolean {
        if (!response?.data || !Array.isArray(response.data) || response.data.length === 0) {
            return false; // No existing records found, no duplicates
        }

        if (locationId === null || locationId === '') {
            // Check if any existing record has null/empty location_id
            return this.hasNullLocationDuplicate(response.data);
        } else {
            // If locationId has a value, any returned record is a duplicate
            return true;
        }
    }

    /**
     * Checks if any record in the response has a null/empty location_id
     * @param records - Array of records from the API response
     * @returns boolean - True if a null location duplicate is found
     */
    private hasNullLocationDuplicate(records: Array<any>): boolean {
        return records.some(record => {
            const existingLocationId = record?.location_id;
            return existingLocationId === null ||
                existingLocationId === undefined ||
                existingLocationId === '';
        });
    }

    /**
     * Creates the UI components configuration object
     * @returns UIComponents configuration
     */
    private createUIComponentsConfiguration(): UIComponents {
        return {
            // Tab components
            tabsetGeneral: this.tabsetGeneral,
            tabRadius: this.tabRadius,
            tabForms: this.tabForms,
            tabTemplates: this.tabTemplates,
            tabOuterForms: this.tabOuterForms,
            tabTree: this.tabTree,
            tabTripData: this.tabTripData,

            // Radius components
            textboxArrivingRadius: this.textboxArrivingRadius,
            textboxArrivedRadius: this.textboxArrivedRadius,
            textboxDepartRadius: this.textboxDepartRadius,

            panelDrvrTechWeb: this.panelDrvrTechWeb,
            panelPeopleNet: this.panelPeopleNet,
            panelQCWorkflow: this.panelQCWorkflow,
            panelPlatformScience: this.panelPlatformScience,
            panelPegasus: this.panelPegasus,
            panelSymphony: this.panelSymphony,
            panelBluetree: this.panelBluetree,

            // UOM labels
            labelArrivingRadiusUOM: this.labelArrivingRadiusUOM,
            labelArrivedRadiusUOM: this.labelArrivedRadiusUOM,
            labelDepartRadiusUOM: this.labelDepartRadiusUOM,

            // Form components
            textboxShipperForm: this.textboxShipperForm,
            textboxStopForm: this.textboxStopForm,
            textboxDriverLoginForm: this.textboxDriverLoginForm,
            textboxConsigneeForm: this.textboxConsigneeForm,
            textboxEmptyShipperForm: this.textboxEmptyShipperForm,
            textboxEmptyConsigneeForm: this.textboxEmptyConsigneeForm,
            textboxArriveShipperForm: this.textboxArriveShipperForm,
            textboxArriveStopForm: this.textboxArriveStopForm,
            textboxArriveConsigneeForm: this.textboxArriveConsigneeForm,
            textboxDepartShipperForm: this.textboxDepartShipperForm,
            textboxDepartStopForm: this.textboxDepartStopForm,
            textboxDepartConsigneeForm: this.textboxDepartConsigneeForm,

            // Template components
            textboxDispatchTemplate: this.textboxDispatchTemplate,
            textboxShipperTemplate: this.textboxShipperTemplate,
            textboxStopTemplate: this.textboxStopTemplate,
            textboxConsigneeTemplate: this.textboxConsigneeTemplate,

            // Location and control fields
            textboxLocationId: this.textboxLocationId,
            textboxMcControlId: this.textboxMcControlId
        };
    }

    /** This is an event handler for the afterExecution event of sourceMcZmitOptions. */
    sourceMcZmitOptionsAfterExecution(event: DataSourceExecutionEvent) {
        if (this.tabOuterForms && this.componentManager.isComponentVisible(this.tabOuterForms.id)) {
            const mc_zmit_options_id = this.getActiveRowString(MobileCommSendLoadOptions.FIELD_NAMES.ID);
            if (mc_zmit_options_id) {
                this.sourceMcMdFormHeader.search({mc_zmit_options_id: mc_zmit_options_id});
            }
        }
    }

    /** This is an event handler for the onSelect event of tableEmbeddedWorkflowTemplates. */
    tableEmbeddedWorkflowTemplatesOnRowExpand(event: TableRowExpansionEvent) {
        const mc_md_form_header_id = event.target.data?.get("id");
        const layoutMcMdFormDetail = event.expandComponents.find(comp => comp.id === "layoutMcMdFormDetailTable") as Layout;
        if (mc_md_form_header_id && layoutMcMdFormDetail) {
            layoutMcMdFormDetail.mainDataSource.mode = this.mainDataSource.mode;
            layoutMcMdFormDetail.mainDataSource.search({mc_md_form_header_id: mc_md_form_header_id});
        }

        const tableMcMdFormDetail = layoutMcMdFormDetail?.components.find(comp => comp.id === "tableMcMdFormDetail") as Table;
        if (tableMcMdFormDetail) {
            tableMcMdFormDetail.addRowDisplayListener((e: TableRowDisplayEvent) => {
                const tableRow = e.getTableRow() as TableRow;
                tableRow.data.set("mc_md_form_header_id", mc_md_form_header_id);
                const formIdTextbox = tableRow.findComponentById("textboxFormId") as Textbox;
                const formTitleTextbox = tableRow.findComponentById("textboxFormTitle") as Textbox;
                formIdTextbox.addBeforeLookupModelSearchListener((lookupEvent) => {
                    lookupEvent.filter.mc_control_id = this.getActiveRowString(MobileCommSendLoadOptions.FIELD_NAMES.MC_CONTROL_ID);
                    lookupEvent.filter.form_type = MobileCommSendLoadOptions.FORM_TYPES.INPUT;
                });

                formIdTextbox.addLookupModelSelectionListener((lookupEvent: LookupModelSelectionEvent) => {
                    formTitleTextbox.text = lookupEvent.selection?.get("title") || "";
                });

                formIdTextbox.addChangeListener((changeEvent: ChangeEvent) => {
                    if (!changeEvent.newValue) {
                        formTitleTextbox.text = "";
                    }
                });
            });
        }
    }

    /** This is an event handler for the onRowDisplay event of tableEmbeddedWorkflowTemplates. */
    tableEmbeddedWorkflowTemplatesOnRowDisplay(event: TableRowDisplayEvent) {
        const row = event.getTableRow();
        const tableNameTextbox = row.findComponentById("textboxTableName") as Textbox;
        const fieldNameTextbox = row.findComponentById("textboxFieldName") as Textbox;
        const fieldValueTextbox = row.findComponentById("textboxFieldValue") as Textbox;

        fieldNameTextbox.enabled = !!row.data?.get("table_name") && !!tableNameTextbox.text;
        fieldValueTextbox.enabled = !!row.data?.get("table_name") && !!row.data?.get("field_name") && !!fieldNameTextbox.text;

        tableNameTextbox.addChangeListener((e: ChangeEvent) => {
            fieldNameTextbox.text = "";
            fieldNameTextbox.enabled = !!e.newValue;
        });

        fieldNameTextbox.addChangeListener((e: ChangeEvent) => {
            fieldValueTextbox.text = "";
            fieldValueTextbox.enabled = !!e.newValue;
        });

        fieldNameTextbox.addBeforeLookupModelSearchListener((e: LookupModelSearchEvent) => {
            const tableNameMap: Record<string, string> = {
                "1": "orders",
                "2": "movement",
                "3": "stop"
            };
            const tableKey = row.data?.get("table_name");
            e.filter.table_name = tableNameMap[tableKey] || "";
        });

        fieldValueTextbox.addBeforeLookupModelSearchListener((e: LookupModelSearchEvent) => {
            e.filter.table_name = row.data?.get("table_name");
            e.filter.field_name = row.data?.get("field_name");
        });

        row.data.set("mc_zmit_options_id", this.getActiveRowString(MobileCommSendLoadOptions.FIELD_NAMES.ID));

        row.expandable = row.mode !== TableRowMode.QUICK_ADD;
    }

    /** This is an event handler for the onChange event of switchTripData. */
    switchTripDataOnChange(event: ChangeEvent) {
        this.tabTripData.visible = !!event.newValue;
    }
}
